/**
 * Harbour pulse view-model — counts only, iceberg vs Needs you cap.
 * Shout unclaimed + overdue so CS cannot miss the queue. Won this week
 * stays a count until the first 0 → 1 this UK week, then celebrates.
 */

import {
  HARBOUR_NEEDS_YOU_CAP,
  formatHarbourNeedsYouCaption,
} from 'src/constants/harbour'
import { HARBOUR_TOOLTIPS } from './harbourTooltips'

export const HARBOUR_PULSE_TONE = Object.freeze({
  DEFAULT: 'default',
  SHOUT: 'shout',
  WON: 'won',
  ICEBERG: 'iceberg',
  MUTED: 'muted',
})

/**
 * @param {{ paidAtRisk?: number, needsYouCap?: number }} [pulse]
 * @returns {string|null}
 */
export function formatHarbourPulseIcebergCaption({
  paidAtRisk,
  needsYouCap = HARBOUR_NEEDS_YOU_CAP,
} = {}) {
  return formatHarbourNeedsYouCaption({
    shown: needsYouCap,
    total: paidAtRisk,
  })
}

/**
 * First loaded sample of a plan filter does not shout (already 2 stays
 * quiet). Shout only when a loaded 0 becomes a count this UK week.
 * @param {{ recoveredThisWeek?: number, previousRecoveredThisWeek?: number|null }} [input]
 */
export function shouldShoutHarbourWonThisWeek({
  recoveredThisWeek,
  previousRecoveredThisWeek,
} = {}) {
  const current = Number(recoveredThisWeek) || 0
  if (current <= 0) {
    return false
  }
  if (previousRecoveredThisWeek == null) {
    return false
  }
  return (Number(previousRecoveredThisWeek) || 0) === 0
}

/**
 * Sticky celebrate after the 0 → N transition, until the count drops back to 0.
 * @param {{
 *   recoveredThisWeek?: number,
 *   previousRecoveredThisWeek?: number|null,
 *   celebrateWon?: boolean,
 * }} [input]
 */
export function nextHarbourWonCelebrateState({
  recoveredThisWeek,
  previousRecoveredThisWeek,
  celebrateWon = false,
} = {}) {
  const recovered = Number(recoveredThisWeek) || 0
  if (recovered <= 0) {
    return false
  }
  if (
    shouldShoutHarbourWonThisWeek({
      recoveredThisWeek: recovered,
      previousRecoveredThisWeek,
    })
  ) {
    return true
  }
  return Boolean(celebrateWon)
}

function tile({
  key,
  label,
  value,
  tip,
  shout = false,
  celebrate = false,
  icebergCaption = null,
}) {
  let tone = HARBOUR_PULSE_TONE.DEFAULT
  if (celebrate && value > 0) {
    tone = HARBOUR_PULSE_TONE.WON
  } else if (shout && value > 0) {
    tone = HARBOUR_PULSE_TONE.SHOUT
  } else if (icebergCaption) {
    tone = HARBOUR_PULSE_TONE.ICEBERG
  } else if (value === 0) {
    tone = HARBOUR_PULSE_TONE.MUTED
  }

  return {
    key,
    label,
    value,
    tip,
    shout: tone === HARBOUR_PULSE_TONE.SHOUT || tone === HARBOUR_PULSE_TONE.WON,
    icebergCaption,
    tone,
  }
}

/**
 * @param {object} [pulse] mapped pulse counts
 * @param {{ celebrateWon?: boolean }} [options]
 */
export function getHarbourPulseTiles(
  pulse = {},
  { celebrateWon = false } = {},
) {
  const needsYouCap = Number(pulse.needsYouCap) || HARBOUR_NEEDS_YOU_CAP
  const paidAtRisk = Number(pulse.paidAtRisk) || 0
  const icebergCaption = formatHarbourPulseIcebergCaption({
    paidAtRisk,
    needsYouCap,
  })

  return [
    tile({
      key: 'paidAtRisk',
      label: 'Paid at risk',
      value: paidAtRisk,
      tip: HARBOUR_TOOLTIPS.paidAtRisk,
      icebergCaption,
    }),
    tile({
      key: 'freeSlipping',
      label: 'Free slipping',
      value: Number(pulse.freeSlipping) || 0,
      tip: HARBOUR_TOOLTIPS.freeSlipping,
    }),
    tile({
      key: 'unclaimedNeedsYou',
      label: 'Unclaimed',
      value: Number(pulse.unclaimedNeedsYou) || 0,
      tip: HARBOUR_TOOLTIPS.unclaimed,
      shout: true,
    }),
    tile({
      key: 'overdueFollowUps',
      label: 'Overdue',
      value: Number(pulse.overdueFollowUps) || 0,
      tip: HARBOUR_TOOLTIPS.overdue,
      shout: true,
    }),
    tile({
      key: 'forgotten',
      label: 'Forgotten',
      value: Number(pulse.forgotten) || 0,
      tip: HARBOUR_TOOLTIPS.forgotten,
    }),
    tile({
      key: 'snoozedThisWeek',
      label: 'Snoozed',
      value: Number(pulse.snoozedThisWeek) || 0,
      tip: HARBOUR_TOOLTIPS.snoozed,
    }),
    tile({
      key: 'recoveredThisWeek',
      label: 'Won this week',
      value: Number(pulse.recoveredThisWeek) || 0,
      tip: HARBOUR_TOOLTIPS.wonThisWeek,
      celebrate: celebrateWon,
    }),
  ]
}
